import { DEFAULT_PER_PAGE } from "@/components/pagination";

import { PageParams } from "@/types/pagination";

/**
 * Calculates a page number based on the navigation direction in a paginated table.
 *
 * @param {number} currentPage - The current page number.
 * @param {string} direction - The navigation direction ('next' or 'previous').
 * @param {number} total - The total number of items.
 * @param {number} perPage - The number of items per page.
 * @returns {number} - The updated page number.
 */
export function updatePage(
  currentPage: number,
  direction: "next" | "previous",
  total: number,
  perPage: number,
): number {
  let page = currentPage;
  if (direction === "next") {
    page = page < Math.ceil(total / perPage) ? page + 1 : page;
  } else {
    page = page > 1 ? page - 1 : page;
  }
  return page;
}

export function handleSortChange<T extends PageParams>(
  field: string,
  filters: T,
  setFilters: (update: T | ((prevFilters: T) => T)) => void,
) {
  if (field === "none") return;
  const flippedDirection = filters.sortDirection === "asc" ? "desc" : "asc";
  const sortDirection = filters.sortBy === field ? flippedDirection : "desc";
  setFilters((prevFilters) => ({
    ...prevFilters,
    sortBy: field,
    sortDirection,
  }));
}

export function handlePageChange<T extends PageParams>(
  direction: string,
  filters: T,
  setFilters: (update: T | ((prevFilters: T) => T)) => void,
  total: number,
) {
  if (!filters.page) {
    return;
  }
  const newPage = updatePage(
    filters.page,
    direction as "next" | "previous",
    total,
    filters.perPage || DEFAULT_PER_PAGE,
  );

  setFilters((prev) => ({
    ...prev,
    page: newPage,
  }));
}
